import React, { useState, useRef, useCallback } from 'react'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { Box, FormLabel, IconButton, Link, Typography } from '@mui/material'
import { CloudUpload, InsertDriveFile, Clear } from '@mui/icons-material'

/**
 * A labelled file dropzone that stores the chosen file's body as base64. It
 * has no form-library dependency, so it renders anywhere the wrapper does.
 */
const JsonFileInput = ({ data, handleChange, path, label, enabled }) => {
    const [fileName, setFileName] = useState('')
    const [readError, setReadError] = useState('')
    const [isDragOver, setIsDragOver] = useState(false)
    const fileInputRef = useRef(null)

    const getBase64DataFromFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            // Split the value because first half is mimetype info, second half is actual data
            reader.onload = () => resolve(reader.result.split(',', 2)[1])
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(file)
        })
    }

    const processFile = useCallback(
        async (file) => {
            if (!file) {
                return
            }
            let val
            try {
                val = await getBase64DataFromFile(file)
            } catch {
                setReadError(`Could not read ${file.name}`)
                return
            }
            setReadError('')
            setFileName(file.name)
            handleChange(path, val)
        },
        [handleChange, path]
    )

    const onFileChange = useCallback(
        async (e) => {
            const targetFile = e.target.files?.[0]
            await processFile(targetFile)
        },
        [processFile]
    )

    // The drag handlers are attached whether or not the control is enabled:
    // a drop the page does not claim is opened by the browser in place of
    // the page.
    const handleDrop = useCallback(
        async (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDragOver(false)
            if (!enabled) {
                return
            }
            const file = e.dataTransfer.files?.[0]
            await processFile(file)
        },
        [enabled, processFile]
    )

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(enabled)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleClick = () => {
        fileInputRef.current?.click()
    }

    const handleRemove = useCallback(
        (e) => {
            e.stopPropagation()
            setFileName('')
            handleChange(path, '')
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        },
        [handleChange, path]
    )

    // Only the file body is stored, so a reopened config has no name or
    // type to show for it. A file chosen here shows by name even when its
    // body is empty.
    const displayName = fileName || (data ? 'Uploaded file' : '')

    return (
        <Box sx={{ mb: 2 }}>
            {label && <FormLabel component="legend">{label}</FormLabel>}
            <input
                type="file"
                ref={fileInputRef}
                onChange={onFileChange}
                disabled={!enabled}
                style={{ display: 'none' }}
            />
            <Box
                onClick={enabled ? handleClick : undefined}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                    border: '1px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'grey.400',
                    borderRadius: 1,
                    backgroundColor: isDragOver
                        ? 'action.hover'
                        : 'background.paper',
                    padding: 1,
                    marginTop: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    cursor: enabled ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': enabled
                        ? {
                              borderColor: 'primary.main',
                              backgroundColor: 'action.hover',
                          }
                        : undefined,
                }}
            >
                {displayName ? (
                    <>
                        <InsertDriveFile color="primary" />
                        <Link
                            variant="body2"
                            href={`data:application/octet-stream;base64,${data}`}
                            download
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayName}
                        </Link>
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            disabled={!enabled}
                        >
                            <Clear fontSize="small" />
                        </IconButton>
                    </>
                ) : (
                    <>
                        <CloudUpload sx={{ color: 'grey.500' }} />
                        <Typography variant="body2" color="textSecondary">
                            Drop a file here, or click to select
                        </Typography>
                    </>
                )}
            </Box>
            {readError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {readError}
                </Typography>
            )}
        </Box>
    )
}

export default withJsonFormsControlProps(JsonFileInput)
